import OrderList from "@DSG/Icons/OrderList";
import UserIcon from "@DSG/Icons/UserIcon";
import colorPallete, { headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
// import { Link } from "react-router-dom";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    recentOrderContainer?: GridItemProps;
    pageTypeLinkGridItem?: GridItemProps;
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    cardTitleContainer?: GridItemProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    pageTypeLinkGridItem: { width: 3 },
    noOrdersFoundGridItem: { width: 12 },
    orderSummaryCard: {
        container: {
            css: css`
            @media screen and (max-width: 1023px) {
                margin:0!important;
                width:100%;
                max-width:100%;
            }
            `,
        },
    },
    recentOrderContainer: {
        css: css`
            max-width: 100%;
            overflow-y: auto;
            & > {
                [class*="GridOffset"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    width:1000px!important;
                                    max-width:1000px;
                                `,
                            ],
                            "max",
                        )}
                }
            }

            @media screen and (max-width: 1023px) {
                margin:0!important;
            }

            /* width */
            ::-webkit-scrollbar {
                width: 6px;
                height: 6px;
            }

            /* Track */
            ::-webkit-scrollbar-track {
                box-shadow: inset 0 0 3px ${colorPallete.grey400};
                border-radius: 6px;
            }

            /* Handle */
            ::-webkit-scrollbar-thumb {
                background: ${colorPallete.grey800};
                border-radius: 6px;
                cursor: pointer;
            }

            /* Handle on hover */
            ::-webkit-scrollbar-thumb:hover {
                background: ${colorPallete.grey200};
            }
        `,
    },
    cardTitleContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            gap: 15px;
            align-items: center;
            margin-top: 40px;
            padding-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                    margin-top: 30px;
                    padding-bottom:15px;
                        gap:0;                                    
                        span {
                            svg {
                                width:30px;
                                height:30px;
                            }
                        }
                    `,
                ],
                "max",
            )}
            h2 {
                margin: 0;
                font: ${headlineFonts.headline3SemiBoldSm.fontWeight} ${headlineFonts.headline3SemiBoldSm.fontSize}/${headlineFonts.headline3SemiBoldSm.lineHeight} ${headlineFonts.headline3SemiBoldSm.fontFamily};
                color: ${colorPallete.grey800};
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
            }
            [class*="GridItemStyle"] {
                justify-content: space-between;
                max-width: 100%;
                flex-basis: 100%;
                padding: 0;
                align-items: center;
            }
            [class*="StyledButton"] {
                color: ${colorPallete.primaryRed500};
                font-weight: 600;
                text-decoration: underline;
                span {
                    color: ${colorPallete.primaryRed500};
                    text-decoration: underline;
                    font-size: 14px;
                    &:hover {
                        color: ${colorPallete.primaryMaroon500};
                    }
                }
            }
        `,
    },
};

const defaultCustomCss = `.card-list-heading{
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <CardList
                    extendedStyles={styles.cardList}
                    data-test-selector="widget_RecentOrders"
                >
                    <StyledWrapper {...styles.cardTitleContainer}>
                            <Icon src={OrderList} width={40} height={40} />
                        <GridItem>
                            <h2>{translate("Recent Orders")}</h2>
                            
                            <Link href={orderHistoryUrl}>{translate("View all orders")}</Link>
                        </GridItem>
                    </StyledWrapper>
                    {ordersDataView.value.length === 0 && (
                        <GridItem className="no-orders-found-grid-item" {...styles.noOrdersFoundGridItem}>
                            <Typography
                                className="no-orders-found-text"
                                {...styles.noOrdersFoundText}
                                data-test-selector="recentOrders_NoOrdersFound"
                            >
                                {translate("No orders found")}
                            </Typography>
                        </GridItem>
                    )}
                    {ordersDataView.value.length > 0 && (
                    <GridContainer {...styles.recentOrderContainer}>
                        <GridItem {...styles.noOrdersFoundGridItem} data-test-selector="orderSummaryCard_header">
                            <GridItem {...styles.pageTypeLinkGridItem}>{translate("Order Date")}</GridItem>
                            <GridItem {...styles.pageTypeLinkGridItem}>{translate("Order Number")}</GridItem>
                            <GridItem {...styles.pageTypeLinkGridItem}>{translate("Status")}</GridItem>
                            <GridItem {...styles.pageTypeLinkGridItem}>{translate("Order Total")}</GridItem>
                        </GridItem>
                        {ordersDataView.value.map(
                            order =>
                                order !== null && (
                                    <CardContainer key={order.id} extendedStyles={styles.cardContainer}>
                                        <OrderSummaryCard
                                            order={order}
                                            orderSettings={settingsCollection.orderSettings}
                                            extendedStyles={styles.orderSummaryCard}
                                        />
                                    </CardContainer>
                                ),
                        )}
                    </GridContainer>
                    )}
                </CardList>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
